import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowUpRight, Bookmark, House, Map, Building2, FileText, Layers, PackageOpen, Trash2 } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useFavourites } from '../context/FavouritesContext';
import ProjectCompletionFurnish from '../components/affiliate/ProjectCompletionFurnish';

/**
 * MyBuildPilotPage
 *
 * The anonymous-friendly "save your place" hub at `/my-buildpilot`.
 *
 * Renders every saved item from the FavouritesContext (plans, land,
 * H&L packages, builders, guides, suburbs) grouped by kind. Requires
 * ZERO authentication - the entire state is localStorage-backed so
 * a visitor who bookmarked a few guides on Monday can come back on
 * Friday and pick up where they left off, without ever signing up.
 *
 * When the user has zero saved items we show a warm empty state
 * that routes them to obvious places to start saving (guides,
 * home designs, builders).
 *
 * Zero email touches. No signup wall. No marketing sequences.
 */

const KIND_ICONS = {
  plan: House,
  land: Map,
  hl_package: Layers,
  builder: Building2,
  article: FileText,
  suburb: Bookmark,
};

const KIND_LABELS = {
  plan: 'Home designs',
  land: 'Land listings',
  hl_package: 'House & land packages',
  builder: 'Builders',
  article: 'Guides & articles',
  suburb: 'Suburbs',
};

const rowDetailPath = (item) => {
  if (item.detail_path) return item.detail_path;
  if (item.kind === 'plan') return `/home-designs/${item.builder_slug || 'design'}/${item.plan_slug || item.full_slug || ''}`;
  if (item.kind === 'land') return `/land-for-sale-adelaide/${item.suburb_slug || 'listing'}/${item.listing_slug || item.slug || ''}`;
  if (item.kind === 'builder') return `/business/${item.builder_slug || item.slug || ''}`;
  if (item.kind === 'suburb') return `/home-builders-in/${item.suburb_slug || item.slug || ''}`;
  return '#';
};

const SavedRow = ({ item, onRemove }) => {
  const Icon = KIND_ICONS[item.kind] || Bookmark;
  const title =
    item.title ||
    item.plan_name ||
    item.name ||
    item.builder_name ||
    item.suburb ||
    item.address ||
    'Saved item';
  const subtitle =
    item.summary ||
    item.builder_name ||
    item.regions_display ||
    item.state ||
    '';

  return (
    <div
      className="group flex items-center justify-between gap-4 rounded-2xl border p-4 transition hover:opacity-90"
      style={{
        borderColor: 'var(--bp-hairline, #e8ebef)',
        backgroundColor: 'var(--bp-surface, #ffffff)',
      }}
      data-testid={`saved-row-${item.kind}-${item.slug || item.builder_slug || item.plan_slug || 'x'}`}
    >
      <Link to={rowDetailPath(item)} className="flex items-center gap-4 flex-1 min-w-0">
        <div
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl"
          style={{ backgroundColor: '#f0f4fa', color: '#1d8efe' }}
        >
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="truncate text-sm font-semibold"
            style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              className="truncate text-xs mt-0.5"
              style={{ color: 'var(--bp-quiet, #86868b)' }}
            >
              {subtitle}
            </div>
          )}
        </div>
        <ArrowUpRight
          className="h-4 w-4 flex-shrink-0 transform transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          style={{ color: 'var(--bp-quiet, #86868b)' }}
          strokeWidth={2.25}
        />
      </Link>
      <button
        type="button"
        onClick={() => onRemove(item)}
        aria-label="Remove"
        className="rounded-full p-2 transition hover:opacity-80"
        style={{ color: 'var(--bp-quiet, #86868b)' }}
        data-testid={`saved-row-remove-${item.slug || item.builder_slug || item.plan_slug || 'x'}`}
      >
        <Trash2 className="h-4 w-4" strokeWidth={2.25} />
      </button>
    </div>
  );
};

const SavedGroup = ({ kind, items, onRemove }) => {
  if (!items || items.length === 0) return null;
  const Icon = KIND_ICONS[kind] || Bookmark;
  return (
    <section className="mb-10" data-testid={`saved-group-${kind}`}>
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color: '#1d8efe' }} strokeWidth={2.25} />
        <h2
          className="text-lg font-semibold"
          style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
        >
          {KIND_LABELS[kind] || kind}
        </h2>
        <span
          className="text-xs font-medium"
          style={{ color: 'var(--bp-quiet, #86868b)' }}
        >
          {items.length}
        </span>
      </div>
      <div className="space-y-3">
        {items.map((item) => (
          <SavedRow
            key={item.__key || `${item.kind}:${item.slug || item.builder_slug || item.plan_slug || Math.random()}`}
            item={item}
            onRemove={onRemove}
          />
        ))}
      </div>
    </section>
  );
};

const EmptyState = () => (
  <div
    className="rounded-3xl border p-10 text-center"
    style={{
      borderColor: 'var(--bp-hairline, #e8ebef)',
      backgroundColor: 'var(--bp-surface-tinted, #f0f4fa)',
    }}
    data-testid="my-buildpilot-empty"
  >
    <div
      className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
      style={{ backgroundColor: '#ffffff', color: '#1d8efe' }}
    >
      <PackageOpen className="h-6 w-6" strokeWidth={2} />
    </div>
    <h2
      className="text-[22px] sm:text-2xl font-semibold mb-2"
      style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
    >
      Nothing saved yet
    </h2>
    <p
      className="max-w-lg mx-auto text-base leading-relaxed mb-6"
      style={{ color: 'var(--bp-quiet, #86868b)' }}
    >
      Any guide, builder, home design, land listing or suburb you save on BuildPilot lands here. No signup needed. Everything is stored on this device.
    </p>
    <div className="flex flex-wrap justify-center gap-2">
      <Link
        to="/guides"
        className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition hover:opacity-90"
        style={{
          backgroundColor: '#0a2540',
          color: '#ffffff',
          borderColor: '#0a2540',
        }}
        data-testid="empty-cta-guides"
      >
        Browse editorial guides
      </Link>
      <Link
        to="/home-designs"
        className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition hover:opacity-90"
        style={{
          backgroundColor: '#ffffff',
          color: 'var(--bp-color-ink, #1d1d1f)',
          borderColor: 'var(--bp-hairline, #e8ebef)',
        }}
        data-testid="empty-cta-designs"
      >
        Browse home designs
      </Link>
      <Link
        to="/where-are-you-at"
        className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition hover:opacity-90"
        style={{
          backgroundColor: '#ffffff',
          color: 'var(--bp-color-ink, #1d1d1f)',
          borderColor: 'var(--bp-hairline, #e8ebef)',
        }}
        data-testid="empty-cta-lanes"
      >
        Find your life stage
      </Link>
    </div>
  </div>
);

const MyBuildPilotPage = () => {
  const {
    plansList,
    landList,
    hlList,
    buildersList,
    articlesList,
    suburbsList,
    count,
    toggleFavourite,
    clearAll,
  } = useFavourites();

  const handleRemove = (item) => {
    toggleFavourite(item, item.kind || 'article');
  };

  const groups = [
    { kind: 'article', items: articlesList },
    { kind: 'plan', items: plansList },
    { kind: 'builder', items: buildersList },
    { kind: 'land', items: landList },
    { kind: 'hl_package', items: hlList },
    { kind: 'suburb', items: suburbsList },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bp-surface, #ffffff)' }}>
      <Helmet>
        <title>My BuildPilot | Your saved plans, builders and guides</title>
        <meta
          name="description"
          content="Your personal BuildPilot hub. Every guide, builder, home design, land listing and suburb you save is here. No signup needed."
        />
        <meta name="robots" content="noindex" />
      </Helmet>

      <Header variant="light" />

      <main data-testid="my-buildpilot-page">
        <section
          className="border-b"
          style={{
            backgroundColor: 'var(--bp-surface-tinted, #f0f4fa)',
            borderColor: 'var(--bp-hairline, #e8ebef)',
          }}
        >
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <div
              className="text-[11px] font-semibold uppercase mb-2"
              style={{ color: '#1d8efe', letterSpacing: '0.18em' }}
            >
              Your personal hub
            </div>
            <h1
              className="text-[32px] sm:text-5xl font-semibold tracking-tight leading-tight mb-3"
              style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
            >
              My BuildPilot
            </h1>
            <p
              className="text-lg max-w-2xl leading-relaxed"
              style={{ color: 'var(--bp-quiet, #86868b)' }}
            >
              Everything you have saved lives here. No account required. If you clear your browser data or use a different device, your saves will not carry across.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12">
          {count === 0 ? (
            <EmptyState />
          ) : (
            <>
              <div className="mb-8 flex items-center justify-between">
                <div
                  className="text-sm font-medium"
                  style={{ color: 'var(--bp-quiet, #86868b)' }}
                >
                  {count} item{count === 1 ? '' : 's'} saved
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Remove every saved item? This cannot be undone.')) {
                      clearAll();
                    }
                  }}
                  className="inline-flex items-center gap-1.5 text-sm font-medium transition hover:opacity-80"
                  style={{ color: 'var(--bp-quiet, #86868b)' }}
                  data-testid="my-buildpilot-clear-all"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={2.25} />
                  Clear all
                </button>
              </div>

              {groups.map((group) => (
                <SavedGroup
                  key={group.kind}
                  kind={group.kind}
                  items={group.items}
                  onRemove={handleRemove}
                />
              ))}
            </>
          )}
        </section>

        {/* Personalised completion trigger — appears once a saved project is
            finishing/finished (journey state), independent of saved items. */}
        <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <ProjectCompletionFurnish />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MyBuildPilotPage;
